/**
 * /market/:slug/eventos — Lista pública de eventos del storefront.
 * Muestra los eventos activos del tenant con cards e info de compra.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { ArrowLeft, CalendarDays, MapPin, Users, Ticket } from "lucide-react";
import { marketSurfaceStyle } from "@/lib/market-theme";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { dbSmGetEvents, type SmEvent } from "@/lib/db-service/events";
import { getRequest } from "@tanstack/react-start/server";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

type EventosPageData = {
  catalog: MarketCatalogResult;
  events: SmEvent[];
};

const fetchEventosData = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<EventosPageData> => {
    const request = getRequest();
    const catalog = await loadMarketCatalog(slug, { request });
    let events: SmEvent[] = [];
    if (catalog.ok) {
      events = await dbSmGetEvents(catalog.data.tenant.id, "active");
    }
    return { catalog, events };
  });

export const Route = createFileRoute("/market_/$slug/eventos")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchEventosData({ data: params.slug }),
  component: EventosPublicPage,
  head: ({ params }) => ({
    meta: [{ title: `Eventos — ${params.slug}` }],
    links: [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
});

function formatEventDate(startsAt: Date): string {
  return format(startsAt, "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es });
}

function EventCard({ event, slug }: { event: SmEvent; slug: string }) {
  const cuposDisponibles = event.capacity - event.soldCount;
  const agotado = cuposDisponibles <= 0;
  const pocosLugares = !agotado && cuposDisponibles <= 10;

  return (
    <article className="overflow-hidden rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface)] shadow-sm transition-shadow hover:shadow-md">
      {/* Imagen de portada */}
      <div className="relative aspect-video w-full overflow-hidden bg-[var(--market-surface-container)]">
        {event.coverImageUrl ? (
          <img
            src={event.coverImageUrl}
            alt={event.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-5xl opacity-20">
            🎟️
          </div>
        )}
        {agotado && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="rounded-full bg-red-500 px-4 py-1.5 text-sm font-bold text-white">
              Sin cupos disponibles
            </span>
          </div>
        )}
        {pocosLugares && !agotado && (
          <span className="absolute right-3 top-3 rounded-full bg-orange-500 px-3 py-1 text-xs font-bold text-white">
            Últimos {cuposDisponibles} cupos
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-col gap-3 p-4">
        <h2 className="market-font-display text-lg font-bold leading-tight text-[var(--market-on-surface)]">
          {event.title}
        </h2>

        {event.description && (
          <p className="line-clamp-2 text-sm text-[var(--market-on-surface-variant)]">
            {event.description}
          </p>
        )}

        <div className="space-y-1.5 text-sm text-[var(--market-on-surface-variant)]">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 shrink-0 text-[var(--market-accent)]" />
            <span className="capitalize">{formatEventDate(event.startsAt)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-[var(--market-accent)]" />
            <span>
              {event.locationType === "online"
                ? "Evento online"
                : event.locationType === "hibrido"
                  ? `Híbrido · ${event.locationAddress ?? ""}`
                  : (event.locationAddress ?? "Presencial")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 shrink-0 text-[var(--market-accent)]" />
            <span>
              {agotado ? "Sin cupos disponibles" : `${cuposDisponibles} cupos disponibles`}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="market-font-display text-xl font-bold text-[var(--market-accent)]">
            {event.price > 0
              ? new Intl.NumberFormat("es-AR", {
                  style: "currency",
                  currency: "ARS",
                  maximumFractionDigits: 0,
                }).format(event.price)
              : "Gratuito"}
          </span>
          <Link
            to="/market/$slug/evento/$eventSlug"
            params={{ slug, eventSlug: event.slug }}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-opacity ${
              agotado
                ? "pointer-events-none bg-[var(--market-surface-variant)] text-[var(--market-on-surface-variant)] opacity-60"
                : "bg-[var(--market-accent)] text-[var(--primary-foreground)] hover:opacity-90 active:scale-95"
            }`}
          >
            <Ticket className="h-4 w-4" />
            {agotado ? "Agotado" : "Comprar entrada"}
          </Link>
        </div>
      </div>
    </article>
  );
}

function EventosPublicPage() {
  const { slug } = Route.useParams();
  const { catalog, events } = Route.useLoaderData() as EventosPageData;

  const offlineScreen = renderMarketPublicOffline(catalog);
  if (offlineScreen) return offlineScreen;

  if (!catalog.ok || !catalog.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{!catalog.ok ? catalog.error : ""}</p>
      </div>
    );
  }

  const { tenant, config } = catalog.data;
  const brandStyle = marketSurfaceStyle({
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor,
    backgroundColor: config.backgroundColor,
  });

  return (
    <div className="market-surface min-h-screen pb-16" style={brandStyle}>
      {/* Header */}
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)] px-4 md:h-16 md:px-12">
        <Link
          to="/market/$slug"
          params={{ slug }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--market-on-surface-variant)] hover:bg-[var(--market-surface-bright)] transition-colors"
          aria-label="Volver a la tienda"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {tenant.logoUrl && (
            <img
              src={tenant.logoUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-lg object-contain bg-[var(--market-surface-bright)] p-0.5 ring-1 ring-[var(--market-surface-variant)]"
            />
          )}
          <span className="market-font-display truncate text-base font-bold text-[var(--market-accent)]">
            {tenant.name}
          </span>
        </div>
      </header>

      {/* Contenido */}
      <div className="mx-auto max-w-4xl px-4 py-8 md:px-12">
        <div className="mb-6">
          <h1 className="market-font-display text-2xl font-extrabold text-[var(--market-on-surface)] md:text-3xl">
            Eventos
          </h1>
          <p className="mt-1 text-sm text-[var(--market-on-surface-variant)]">
            Próximos eventos de {tenant.name}
          </p>
        </div>

        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-[var(--market-surface-variant)] py-20 text-center">
            <span className="text-5xl opacity-30">🎟️</span>
            <p className="text-lg font-semibold text-[var(--market-on-surface)]">
              Sin eventos próximos
            </p>
            <p className="text-sm text-[var(--market-on-surface-variant)]">
              Volvé pronto para ver los próximos eventos.
            </p>
            <Link
              to="/market/$slug"
              params={{ slug }}
              className="mt-2 rounded-full bg-[var(--market-accent)] px-6 py-2.5 text-sm font-bold text-[var(--primary-foreground)]"
            >
              Ir a la tienda
            </Link>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((ev) => (
              <EventCard key={ev.id} event={ev} slug={slug} />
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 border-t border-[var(--market-surface-variant)]">
        <StructuraPlatformSignature />
      </div>
    </div>
  );
}
